import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage, translations } from '../context/LanguageContext';

const { FiHeart, FiStar, FiSunrise, FiSave, FiArchive, FiCalendar, FiChevronLeft, FiChevronRight } = FiIcons;

const ReflectionSection = ({ reflections, onUpdateReflection }) => {
  const { language } = useLanguage();
  const t = translations[language];
  const [showArchive, setShowArchive] = useState(false);
  const [currentArchiveIndex, setCurrentArchiveIndex] = useState(0);
  const [archiveEntries, setArchiveEntries] = useState(() => {
    const saved = localStorage.getItem('reflectionArchive');
    return saved ? JSON.parse(saved) : [];
  });

  const reflectionFields = [
    {
      key: 'gratitude',
      title: t.gratitude,
      icon: FiHeart,
      placeholder: t.gratitudePlaceholder,
      color: 'text-pink-300'
    },
    {
      key: 'learnings',
      title: t.keyLearnings,
      icon: FiStar,
      placeholder: t.learningsPlaceholder,
      color: 'text-yellow-300'
    },
    {
      key: 'tomorrow',
      title: t.tomorrowFocus,
      icon: FiSunrise,
      placeholder: t.tomorrowPlaceholder,
      color: 'text-orange-300'
    }
  ];

  const saveToArchive = () => {
    // Check if there's content to save
    const hasContent = Object.values(reflections).some(text => text.trim() !== '');
    
    if (!hasContent) {
      alert(t.noContentToSave);
      return;
    }
    
    const newEntry = {
      id: Date.now(),
      date: new Date().toISOString(),
      ...reflections
    };
    
    const updatedArchive = [newEntry, ...archiveEntries];
    setArchiveEntries(updatedArchive);
    localStorage.setItem('reflectionArchive', JSON.stringify(updatedArchive));
    
    alert(t.reflectionSaved);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const navigateArchive = (direction) => {
    if (direction === 'prev' && currentArchiveIndex < archiveEntries.length - 1) {
      setCurrentArchiveIndex(currentArchiveIndex + 1);
    } else if (direction === 'next' && currentArchiveIndex > 0) {
      setCurrentArchiveIndex(currentArchiveIndex - 1);
    }
  };

  const currentArchiveEntry = archiveEntries[currentArchiveIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <SafeIcon icon={showArchive ? FiArchive : FiHeart} className="text-2xl text-pink-300 mr-3" />
          <h2 className="text-2xl font-bold text-white">
            {showArchive ? t.reflectionArchive : t.eveningReflection}
          </h2>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={() => setShowArchive(!showArchive)}
            className="px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-all duration-200 flex items-center"
          >
            <SafeIcon icon={showArchive ? FiHeart : FiArchive} className="mr-2" />
            <span>{showArchive ? t.currentReflection : t.viewArchive}</span>
          </button>
          
          {!showArchive && (
            <button
              onClick={saveToArchive}
              className="px-3 py-2 bg-pink-500/20 text-pink-100 rounded-lg hover:bg-pink-500/30 transition-all duration-200 flex items-center"
            >
              <SafeIcon icon={FiSave} className="mr-2" />
              <span>{t.saveReflection}</span>
            </button>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showArchive ? (
          <motion.div
            key="archive"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            {archiveEntries.length === 0 ? (
              <div className="text-center py-8 text-white/70">
                <SafeIcon icon={FiArchive} className="text-4xl mb-3 mx-auto" />
                <p>{t.noSavedReflections}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4 bg-white/5 p-3 rounded-lg">
                  <button
                    onClick={() => navigateArchive('prev')}
                    disabled={currentArchiveIndex >= archiveEntries.length - 1}
                    className={`p-2 rounded-full ${
                      currentArchiveIndex >= archiveEntries.length - 1
                        ? 'text-white/30 bg-white/5'
                        : 'text-white/70 bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    <SafeIcon icon={FiChevronLeft} />
                  </button>
                  
                  <div className="flex items-center text-white">
                    <SafeIcon icon={FiCalendar} className="mr-2 text-pink-300" />
                    <span>
                      {formatDate(currentArchiveEntry.date)}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => navigateArchive('next')}
                    disabled={currentArchiveIndex === 0}
                    className={`p-2 rounded-full ${
                      currentArchiveIndex === 0
                        ? 'text-white/30 bg-white/5'
                        : 'text-white/70 bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    <SafeIcon icon={FiChevronRight} />
                  </button>
                </div>

                {reflectionFields.map((field, index) => (
                  <motion.div
                    key={field.key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex items-center mb-2">
                      <SafeIcon icon={field.icon} className={`text-lg mr-2 ${field.color}`} />
                      <h3 className="text-white font-medium">{field.title}</h3>
                    </div>
                    <div className="w-full bg-white/5 text-white px-4 py-3 rounded-lg border border-white/20 min-h-[80px]">
                      {currentArchiveEntry[field.key] || <span className="text-white/40">{t.noContent}</span>}
                    </div>
                  </motion.div>
                ))}
                
                <div className="text-center text-white/60 text-sm mt-4">
                  {t.entryCount.replace('{current}', currentArchiveIndex + 1).replace('{total}', archiveEntries.length)}
                </div>
              </>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="current"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="space-y-4"
          >
            {reflectionFields.map((field, index) => (
              <motion.div
                key={field.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center mb-2">
                  <SafeIcon icon={field.icon} className={`text-lg mr-2 ${field.color}`} />
                  <h3 className="text-white font-medium">{field.title}</h3>
                </div>
                <textarea
                  value={reflections[field.key]}
                  onChange={(e) => onUpdateReflection(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  className="w-full bg-white/5 text-white placeholder-white/60 px-4 py-3 rounded-lg border border-white/20 focus:outline-none focus:border-pink-400 resize-none h-20"
                />
              </motion.div>
            ))}

            <div className="mt-6 p-4 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-lg border border-pink-500/30">
              <p className="text-pink-200 text-sm text-center italic">
                "{t.reflectionQuote}"
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ReflectionSection;